import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Button } from 'foundations-components/transitional/components/Button';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { ExternalLinkIcon } from 'foundations-assets/icons/components/ExternalLinkIcon';
import CitationPill from 'ai-components-ui-library/components/citation/CitationPill';
import { useCallback, useState } from 'react';
import { useIsUngated } from '../../../hooks/useIsUngated';
import { gate } from 'hub-http/gates';
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
function getTooltipContent(primaryText, secondaryText) {
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [primaryText, primaryText && secondaryText && ' | ', secondaryText]
  });
}
function SourceContent({
  icon,
  secondaryText,
  primaryText,
  locationText,
  inlineSize,
  locationEnabled
}) {
  const locationCitationPill = locationText ? /*#__PURE__*/_jsx(CitationPill, {
    label: locationText,
    variant: "outlined"
  }) : null;
  const renderedLocationWithinSource = locationEnabled ? /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    gap: "50",
    overflow: "hidden",
    children: [secondaryText && /*#__PURE__*/_jsx(Text, {
      as: "p",
      typography: "heading-25",
      color: "text-core-default",
      margin: "0",
      children: /*#__PURE__*/_jsx(TruncateString, {
        matchContentWidth: true,
        tooltip: secondaryText,
        textAlign: "start",
        children: secondaryText
      })
    }), primaryText && /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "row",
      gap: "200",
      alignItems: "center",
      children: [/*#__PURE__*/_jsx(Text, {
        typography: secondaryText ? 'detail-100' : 'heading-25',
        color: "text-core-default",
        children: primaryText
      }), locationCitationPill]
    })]
  }) : /*#__PURE__*/_jsx(Text, {
    as: "p",
    typography: "heading-25",
    color: "text-core-default",
    margin: "0",
    children: /*#__PURE__*/_jsxs(TruncateString, {
      matchContentWidth: true
      // The secondary text shows with low contrast against the tooltip so we correct this by
      // using a helper that returns the plain text content of the tooltip.
      ,
      tooltip: getTooltipContent(primaryText, secondaryText),
      textAlign: "start",
      children: [/*#__PURE__*/_jsx(Text, {
        as: "span",
        children: primaryText
      }), primaryText && secondaryText && /*#__PURE__*/_jsx(Text, {
        as: "span",
        typography: "detail-100",
        color: "text-core-default",
        children: ' | '
      }), /*#__PURE__*/_jsx(Text, {
        as: "span",
        typography: "detail-100",
        color: "text-core-default",
        children: secondaryText
      })]
    })
  });
  return /*#__PURE__*/_jsxs(Flex, {
    justifyContent: "start",
    alignItems: "center",
    gap: "200",
    overflow: "hidden",
    inlineSize: inlineSize,
    children: [icon && /*#__PURE__*/_jsx(Flex, {
      flexShrink: "0",
      display: "inline-flex",
      alignItems: "center",
      children: icon
    }), /*#__PURE__*/_jsx(View, {
      textAlign: "start",
      children: renderedLocationWithinSource
    })]
  });
}
function Source({
  primaryText,
  icon,
  secondaryText,
  locationText,
  data,
  onClick,
  href,
  disabled = false,
  inlineSize = 'auto'
}) {
  const [isHovered, setIsHovered] = useState(false);
  const isUngatedForLocationWithinSource = useIsUngated(gate('KnowledgeBox:LocationWithinSource'));
  const handleClick = useCallback(() => {
    onClick === null || onClick === void 0 || onClick(data);
  }, [onClick, data]);
  const handleKeyDown = useCallback(event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  }, [handleClick]);
  const isClickable = !disabled && (!!onClick || !!href);
  if (isClickable) {
    return /*#__PURE__*/_jsx(Flex, {
      alignItems: "center",
      backgroundColor: isHovered ? 'fill-secondary-hover' : undefined,
      borderRadius: "200",
      paddingInline: "300",
      paddingBlock: "200",
      onMouseEnter: () => setIsHovered(true),
      onMouseLeave: () => setIsHovered(false),
      children: /*#__PURE__*/_jsx(Button, {
        use: "unstyled",
        onClick: handleClick,
        onKeyDown: handleKeyDown,
        block: true,
        borderRadius: "200",
        width: "100%",
        href: href,
        external: !!href,
        hideExternalLinkIcon: true,
        children: /*#__PURE__*/_jsxs(Flex, {
          inlineSize: "100%",
          justifyContent: "start",
          alignItems: "center",
          gap: "200",
          children: [/*#__PURE__*/_jsx(Flex, {
            flexGrow: 1,
            minInlineSize: "0",
            children: /*#__PURE__*/_jsx(SourceContent, {
              icon: icon,
              secondaryText: secondaryText,
              primaryText: primaryText,
              locationText: locationText,
              inlineSize: inlineSize,
              locationEnabled: isUngatedForLocationWithinSource
            })
          }), /*#__PURE__*/_jsx(Flex, {
            flexShrink: 0,
            alignItems: "center",
            visibility: isHovered ? 'visible' : 'hidden',
            children: /*#__PURE__*/_jsx(ExternalLinkIcon, {
              size: "300"
            })
          })]
        })
      })
    });
  }
  return /*#__PURE__*/_jsx(View, {
    paddingInline: "300",
    paddingBlock: "200",
    borderRadius: "200",
    children: /*#__PURE__*/_jsx(SourceContent, {
      icon: icon,
      secondaryText: secondaryText,
      primaryText: primaryText,
      inlineSize: inlineSize
    })
  });
}
Source.displayName = 'Source';
export { Source };